'use client';
import React from 'react';
import Script from 'next/script';
import dynamic from "next/dynamic";
import Navigation from '../../components/Navigation';
import RandomBackground from '../../components/RandomBackground';

const FloatingAnchorMenu = dynamic(
  () => import("@/app/components/FloatingAnchorMenu"),
  { ssr: false },
);

function GlassCard({
  children,
  className = '',
  id,
}: Readonly<{ children: React.ReactNode; className?: string; id?: string }>) {
  return (
    <div id={id} className={`rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 ${className}`}>
      {children}
    </div>
  );
}

export default function ComparisonPage() {
  const floatingMenuItems = [
    { href: '#overview', text: 'Overview' },
    { href: '#comparison-chart', text: 'Comparison Chart' },
    { href: '#design-innovation', text: 'Design & Innovation' },
    { href: '#durability', text: 'Durability' },
    { href: '#ease-of-use', text: 'Ease of Use' },
    { href: '#value-cost', text: 'Value & Cost' },
    { href: '#customer-feedback', text: 'Customer Feedback' },
    { href: '#bottom-line', text: 'Bottom Line' },
  ];

  const comparisonSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Rain Block® vs Twist and Seal: Complete Comparison Guide",
    "description": "Detailed comparison between Rain Block® waterproof outlet covers and Twist and Seal competitor products",
    "image": "https://rainblock.net/hydroponics.webp",
    "author": {
      "@type": "Organization",
      "name": "Rain Block®"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-09",
    "articleBody": "Comprehensive comparison of waterproof outlet protection products including durability, ease of use, design, and value"
  };

  return (
    <>
      <Script
        id="comparison-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(comparisonSchema) }}
      />
      
      <div className="relative min-h-screen text-white overflow-hidden">
        <Navigation />
        {/* Fixed background */}
        <RandomBackground className="brightness-110 saturate-110" />
        {/* Readability overlays */}
        <div
          aria-hidden
          className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10"
        />
        <div
          aria-hidden
          className="fixed inset-0 -z-10 pointer-events-none"
          style={{
            background: "radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
          }}
        />
        <FloatingAnchorMenu items={floatingMenuItems} scrollShift={-180} />

        {/* Content */}
        <main className="relative z-10 pt-40 pb-24 px-8 xl:pt-24">
          <div className="mx-auto max-w-5xl">
            {/* Header */}
            <div id="overview" className="mb-12">
              <h1 className="text-5xl font-extrabold mb-6">
                Rain Block<sup className="tm-reg">&reg;</sup> vs <span className="text-[#66ccff]">Twist and Seal</span>
              </h1>
              <p className="text-lg text-white/85 mb-4">
                Both products protect outdoor electrical connections, but there are significant differences in design, durability, and value. Here&apos;s our comprehensive comparison.
              </p>
              <div className="flex items-center gap-4 text-white/80">
                <span>ðŸ“Š Detailed Comparison</span>
                <span>â€¢</span>
                <span>â±ï¸ 6 min read</span>
              </div>
            </div>

            {/* Quick Comparison Table */}
            <GlassCard id="comparison-chart" className="p-8 mb-12 overflow-x-auto">
              <h2 className="text-2xl font-bold mb-6">Quick Comparison Chart</h2>
              <table className="w-full text-white/80 text-sm">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 font-bold">Feature</th>
                    <th className="text-center py-3 px-4 font-bold">Rain Block<sup className="tm-reg">&reg;</sup></th>
                    <th className="text-center py-3 px-4 font-bold">Twist & Seal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr>
                    <td className="py-4 px-4"><strong>100% Waterproof Seal</strong></td>
                    <td className="text-center py-4 px-4"><span className="text-[#66ccff] font-bold">âœ“ Yes</span></td>
                    <td className="text-center py-4 px-4"><span className="text-white/80">Partial</span></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Design Patent</strong></td>
                    <td className="text-center py-4 px-4"><span className="text-[#66ccff] font-bold">âœ“ Yes</span></td>
                    <td className="text-center py-4 px-4">-</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Reusable (5+ Years)</strong></td>
                    <td className="text-center py-4 px-4"><span className="text-[#66ccff] font-bold">âœ“ Yes</span></td>
                    <td className="text-center py-4 px-4"><span className="text-white/80">Limited</span></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Works with Any Extension Cord</strong></td>
                    <td className="text-center py-4 px-4"><span className="text-[#66ccff] font-bold">âœ“ Yes</span></td>
                    <td className="text-center py-4 px-4"><span className="text-white/80">Varies</span></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Installation Time</strong></td>
                    <td className="text-center py-4 px-4"><strong>3-5 seconds</strong></td>
                    <td className="text-center py-4 px-4"><strong>15-30 seconds</strong></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Ease of Removal</strong></td>
                    <td className="text-center py-4 px-4"><span className="text-[#66ccff] font-bold">âœ“ Effortless</span></td>
                    <td className="text-center py-4 px-4"><span className="text-white/80">Difficult</span></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Price per Unit</strong></td>
                    <td className="text-center py-4 px-4"><strong>$9.99</strong></td>
                    <td className="text-center py-4 px-4"><strong>$14.99+</strong></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4"><strong>Customer Rating (Avg)</strong></td>
                    <td className="text-center py-4 px-4"><strong>4.9/5 â­</strong></td>
                    <td className="text-center py-4 px-4"><strong>3.2/5 â­</strong></td>
                  </tr>
                </tbody>
              </table>
            </GlassCard>

            {/* Design & Innovation */}
            <section id="design-innovation" className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Design & Innovation</h2>
              
              <GlassCard className="p-8 mb-6">
                <h3 className="text-2xl font-bold text-[#66ccff] mb-4">ðŸ† Rain Block<sup className="tm-reg">&reg;</sup> - Patented Gel Gasket</h3>
                <p className="text-white/80 mb-4">
                  Rain Block®&apos;s design patent (US Patent Pending) features a proprietary gel gasket that inserts between the extension cord plug and your appliance, light, or decoration plug to create a waterproof sealâ€”protecting the most vulnerable point for water infiltration.
                </p>
                <ul className="space-y-3 text-white/75">
                  <li className="flex gap-3">
                    <span className="text-[#66ccff]">âœ“</span>
                    <span>Super-soft gel-like material adapts perfectly to any connection shape</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff]">âœ“</span>
                    <span>Installs between plug and outlet for complete seal</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff]">âœ“</span>
                    <span>Tested to IP67 waterproof standards</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff]">âœ“</span>
                    <span>UV and weather-resistant for 5+ years of outdoor use</span>
                  </li>
                </ul>
              </GlassCard>

              <GlassCard className="p-8">
                <h3 className="text-2xl font-bold text-[#66ccff] mb-4">Twist and Seal - Traditional Approach</h3>
                <p className="text-white/80 mb-4">
                  Twist and Seal covers use a simpler screw-on design that relies on manual tightening to create a seal.
                </p>
                <ul className="space-y-3 text-white/75">
                  <li className="flex gap-3">
                    <span className="text-white/80">âš ï¸</span>
                    <span>Requires manual twisting and precise tightening</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-white/80">âš ï¸</span>
                    <span>Seal degrades over time with repeated use</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-white/80">âš ï¸</span>
                    <span>Can overtighten, damaging connectors</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-white/80">âš ï¸</span>
                    <span>Limited compatibility with non-standard plugs</span>
                  </li>
                </ul>
              </GlassCard>
            </section>

            {/* Durability & Longevity */}
            <section id="durability" className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Durability & Longevity</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <GlassCard className="p-8">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-4">Rain Block<sup className="tm-reg">&reg;</sup> Durability</h3>
                  <div className="space-y-4 text-white/75">
                    <div>
                      <p className="font-semibold text-white mb-2">Expected Lifespan:</p>
                      <p>5-10+ years with normal use</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Material Quality:</p>
                      <p>Medical-grade silicone, UV-stabilized</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Seasonal Reuse:</p>
                      <p>Install and remove 100+ times without degradation</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Weather Resistance:</p>
                      <p>-40Â°C to +80Â°C operational range</p>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard className="p-8">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-4">Twist & Seal Durability</h3>
                  <div className="space-y-4 text-white/75">
                    <div>
                      <p className="font-semibold text-white mb-2">Expected Lifespan:</p>
                      <p>2-3 years average, often needs replacement</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Material Quality:</p>
                      <p>Standard rubber, prone to cracking</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Seasonal Reuse:</p>
                      <p>10-20 install/remove cycles before seal weakens</p>
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-2">Weather Resistance:</p>
                      <p>-20Â°C to +60Â°C (limited cold tolerance)</p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </section>

            {/* Ease of Use */}
            <section id="ease-of-use" className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Ease of Installation & Use</h2>
              
              <GlassCard className="p-8 mb-6">
                <h3 className="text-xl font-bold text-[#66ccff] mb-4">Rain Block<sup className="tm-reg">&reg;</sup> Installation</h3>
                <p className="text-white/80 mb-4"><strong>Time Required:</strong> 3-5 seconds per connection</p>
                <ol className="space-y-3 text-white/75 list-decimal list-inside">
                  <li>Insert Rain Block&reg; gel gasket between the extension cord plug and your appliance/light/decoration plug</li>
                  <li>Plug the appliance into the extension cordâ€”the gasket seats between them</li>
                  <li>Waterproof seal is automatically createdâ€”you&apos;re done!</li>
                </ol>
                <p className="text-[#66ccff] mt-6 font-semibold">âœ“ No twisting, no tools, no adjustments needed</p>
              </GlassCard>

              <GlassCard className="p-8">
                <h3 className="text-xl font-bold text-[#66ccff] mb-4">Twist & Seal Installation</h3>
                <p className="text-white/80 mb-4"><strong>Time Required:</strong> 15-30 seconds per connection</p>
                <ol className="space-y-3 text-white/75 list-decimal list-inside">
                  <li>Plug in your extension cord or device</li>
                  <li>Place cover over connection point</li>
                  <li>Twist clockwise until &quot;tight&quot; (but not too tight)</li>
                  <li>Re-check tension periodically</li>
                </ol>
                <p className="text-white/60 mt-6 font-semibold">âš ï¸ Risk of over-tightening and damaging connectors</p>
              </GlassCard>
            </section>

            {/* Value & Cost */}
            <section id="value-cost" className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Value & Total Cost of Ownership</h2>
              
              <GlassCard className="p-8 mb-6 border-[#66ccff]/30 bg-[#66ccff]/5">
                <h3 className="text-xl font-bold text-[#66ccff] mb-4">Rain Block<sup className="tm-reg">&reg;</sup> Cost Analysis</h3>
                <div className="space-y-4 text-white/80">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>5-pack:</span>
                    <span className="font-bold">$4.50</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>10-pack:</span>
                    <span className="font-bold">$6.49</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>50-pack:</span>
                    <span className="font-bold">$23.99</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>100-pack:</span>
                    <span className="font-bold">$44.99</span>
                  </div>
                  <div className="flex justify-between items-center py-3 pt-4 border-t-2 border-[#66ccff]/30">
                    <span className="font-semibold">Cost per unit (5-pack):</span>
                    <span className="font-bold text-[#66ccff]">$0.90/unit</span>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <h3 className="text-xl font-bold text-[#66ccff] mb-4">Twist & Seal Cost Analysis</h3>
                <div className="space-y-4 text-white/80">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>Single unit:</span>
                    <span className="font-bold">$14.99</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>Replacement set (after 2-3 years):</span>
                    <span className="font-bold">$14.99</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span>Over 5 years (need ~2 replacement sets):</span>
                    <span className="font-bold">$44.97</span>
                  </div>
                  <div className="flex justify-between items-center py-3 pt-4 border-t-2 border-white/10">
                    <span className="font-semibold">Cost per year (over 5 years):</span>
                    <span className="font-bold text-white/60">$8.99/unit</span>
                  </div>
                </div>
              </GlassCard>
            </section>

            {/* Customer Experience */}
            <section id="customer-feedback" className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Real Customer Feedback</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <GlassCard className="p-6 border-[#66ccff]/30 bg-[#66ccff]/5">
                  <div className="flex gap-2 mb-3">
                    {[1, 2, 3, 4, 5].map((i) => <span key={i} className="text-[#66ccff]">â­</span>)}
                  </div>
                  <p className="text-white/80 mb-3">&quot;Rain Block® is so simple and effective. Plug, slide, done! Best investment for protecting my Christmas lights.&quot;</p>
                  <p className="text-white/60">â€” Jennifer M., Verified Customer</p>
                </GlassCard>

                
              </div>

              <GlassCard className="p-6 border-[#66ccff]/30 bg-[#66ccff]/5">
                <div className="flex gap-2 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => <span key={i} className="text-[#66ccff]">â­</span>)}
                </div>
                <p className="text-white/80 mb-3">&quot;Five years and counting! Still waterproof, still easy to install. The seal is amazing. Definitely recommend over other brands.&quot;</p>
                <p className="text-white/60">â€” Robert T., Long-term Rain Block® User</p>
              </GlassCard>
            </section>

            {/* Bottom Line */}
            <section id="bottom-line" className="mb-12">
              <GlassCard className="p-12 text-center border-[#66ccff]/30 bg-[#66ccff]/10">
                <h2 className="text-3xl font-bold mb-6">The Bottom Line</h2>
                <p className="text-lg text-white/75 mb-8 max-w-3xl mx-auto">
                  While both products offer waterproof protection, <strong>Rain Block<sup className="tm-reg">&reg;</sup></strong> stands out with its patented design, superior durability, ease of use, and lower long-term cost. It&apos;s the clear choice for anyone serious about protecting their outdoor electrical connections season after season.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="/shop"
                    className="rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300"
                    style={{ textShadow: "none" }}
                  >
                    Shop Now
                  </a>
                  <a 
                    href="/guides/best-weatherproof-extension-cords"
                    className="py-3 px-8 rounded-full border border-white/20 bg-white/10 text-white font-bold hover:bg-white/15 transition-all duration-300"
                  >
                    Back to Buying Guide
                  </a>
                </div>
              </GlassCard>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}







